import React, { useState, useCallback } from 'react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Switch } from '@/shared/components/ui/Switch';
import { Button } from '@/shared/components/ui/Button';
import { useEmailDomainMutations } from '../hooks';
import { Icon } from '@/shared/icons'
interface CreateDomainDialogProps {
  /** Se o dialog esta aberto */
  isOpen: boolean;
  /** Callback para fechar o dialog */
  onClose: () => void;
  /** Callback chamado apos criacao bem-sucedida, recebe o id do dominio criado */
  onSuccess?: (id: string) => void;
}

/**
 * Dialog para criar um novo dominio de email.
 *
 * Campos:
 * - Nome do dominio (obrigatorio)
 * - Regiao (opcional)
 * - Envio habilitado (toggle, default: true)
 * - Recebimento habilitado (toggle, default: true)
 *
 * @example
 * ```tsx
 * <CreateDomainDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSuccess={() => toast.success('Dominio criado!')}
 * />
 * ```
 */
export const CreateDomainDialog: React.FC<CreateDomainDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [domain, setDomain] = useState('');
  const [region, setRegion] = useState('');
  const [sendingEnabled, setSendingEnabled] = useState(true);
  const [receivingEnabled, setReceivingEnabled] = useState(true);
  const [error, setError] = useState('');

  const { createDomain } = useEmailDomainMutations();

  const resetForm = useCallback(() => {
    setDomain('');
    setRegion('');
    setSendingEnabled(true);
    setReceivingEnabled(true);
    setError('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedDomain = domain.trim().toLowerCase();
    if (!trimmedDomain) {
      setError('O dominio e obrigatorio.');
      return;
    }

    // Validacao basica de formato de dominio
    const domainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;
    if (!domainRegex.test(trimmedDomain)) {
      setError('Formato de dominio invalido. Ex: empresa.com.br');
      return;
    }

    try {
      const created = await createDomain.mutateAsync({
        domain: trimmedDomain,
        sendingEnabled,
        receivingEnabled,
        ...(region ? { region } : {}),
      });
      resetForm();
      onSuccess?.(created.id);
      onClose();
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { message?: string } } };
      setError(apiError?.response?.data?.message || 'Erro ao criar dominio. Tente novamente.');
    }
  }, [domain, region, sendingEnabled, receivingEnabled, createDomain, resetForm, onSuccess, onClose]);

  // Footer — Button shared: Cancelar = secondary (F71), primário com loading
  const footerContent = (
    <>
      <Button
        type="button"
        variant="secondary"
        onClick={handleClose}
        disabled={createDomain.isPending}
        data-testid="create-domain-cancel"
      >
        Cancelar
      </Button>
      <Button
        type="submit"
        form="create-domain-form"
        variant="primary"
        loading={createDomain.isPending}
        data-testid="create-domain-submit"
      >
        {createDomain.isPending ? 'Criando...' : 'Adicionar Dominio'}
      </Button>
    </>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      icon={<Icon name="globe" className="w-4 h-4" />}
      title="Adicionar Dominio"
      disabled={createDomain.isPending}
      footer={footerContent}
    >
      <form
        id="create-domain-form"
        onSubmit={handleSubmit}
        className="p-[18px]"
        style={{
          '--theme-text': '#1e293b',
          '--theme-muted-text': '#6b7280',
          '--theme-input-background': '#ffffff',
          '--theme-input-text': '#1e293b',
          '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
          '--theme-input-focus': '#8b5cf6',
          '--theme-error-color': '#ef4444',
        } as React.CSSProperties}
      >
          <div className="flex flex-col gap-4">
            {/* Dominio */}
            <div>
              <label
                className="block text-sm font-medium mb-1.5"
                style={{ color: '#1e293b' }}
              >
                Dominio <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <Input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="exemplo.com.br"
                error={error || undefined}
                autoFocus
              />
              {error && (
                <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                  {error}
                </p>
              )}
            </div>

            {/* Regiao */}
            <div>
              <label
                className="block text-sm font-medium mb-1.5"
                style={{ color: '#1e293b' }}
              >
                Regiao
              </label>
              <Select
                value={region}
                onChange={(value) => setRegion(value)}
                placeholder="Selecione uma regiao (opcional)"
                options={[
                  { value: 'us-east-1', label: 'US East (Virginia)' },
                  { value: 'eu-west-1', label: 'EU West (Irlanda)' },
                  { value: 'sa-east-1', label: 'SA East (Sao Paulo)' },
                ]}
              />
            </div>

            {/* Toggles */}
            <div className="flex flex-col gap-3">
              {/* Envio — Switch shared 38×22 (F82) */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                    Habilitar envio
                  </p>
                  <p className="text-xs" style={{ color: '#64748b' }}>
                    Permite enviar emails a partir deste dominio
                  </p>
                </div>
                <span className="flex-shrink-0" data-testid="create-domain-sending-toggle">
                  <Switch checked={sendingEnabled} onChange={setSendingEnabled} />
                </span>
              </div>

              {/* Recebimento — Switch shared 38×22 (F82) */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
                    Habilitar recebimento
                  </p>
                  <p className="text-xs" style={{ color: '#64748b' }}>
                    Permite receber emails neste dominio
                  </p>
                </div>
                <span className="flex-shrink-0" data-testid="create-domain-receiving-toggle">
                  <Switch checked={receivingEnabled} onChange={setReceivingEnabled} />
                </span>
              </div>
            </div>
          </div>
      </form>
    </Drawer>
  );
};

CreateDomainDialog.displayName = 'CreateDomainDialog';
